import { Inject, Injectable } from '@angular/core';
import { ReplaySubject, from, Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { includesAny } from '@nk/utils-lib';
import { Auth0Callback, AuthorizeOptions, ChangePasswordOptions, WebAuth } from 'auth0-js';
import { StorageService } from '@nk/storage-lib';
import {
  AppAuthConfig,
  APP_AUTH_CONFIG_TOKEN,
  AuthInfo,
  AuthResult,
  isLoginMessage,
  LOGIN_MESSAGES,
} from '../utils/auth.utils';
import { GenericAuthService } from '../utils/generic-auth-service.utils';
import { SessionService } from './session.service';
import posthog from 'posthog-js';

@Injectable()
export class AppAuthService extends SessionService implements GenericAuthService {
  private _authInfoSubject = new ReplaySubject<AuthInfo>(1);
  public authInfo$ = this._authInfoSubject.asObservable();
  public authInfo: AuthInfo | undefined;

  public clientId: string | undefined;
  public loginUrl = window.location.protocol + '//' + window.location.host + '/login';
  public logoutUrl = window.location.protocol + '//' + window.location.host + '/logout-success';
  public loginMessage: string | undefined;
  public webAuth: WebAuth | undefined;
  public loginMessageDict = LOGIN_MESSAGES;

  public redirectUrl: string | null = null;

  constructor(
    private _router: Router,
    private _storageService: StorageService,
    @Inject(APP_AUTH_CONFIG_TOKEN) private _appAuthConfig: AppAuthConfig,
  ) {
    super();
    if (!this._appAuthConfig) {
      throw new Error('APP_AUTH_CONFIG_TOKEN was not provided');
    }
    this.clientId = this._appAuthConfig.clientId;
    this.webAuth = new WebAuth({
      clientID: this._appAuthConfig.clientId,
      domain: this._appAuthConfig.domain,
      responseType: this._appAuthConfig.responseType,
      audience: this._appAuthConfig.audience,
      scope: this._appAuthConfig.scope,
      redirectUri: this.loginUrl,
    });
    this.webAuth.checkSession({}, (err, authResult: AuthResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        const now = new Date().getTime();
        const expiresAt = JSON.stringify(now + authResult.expiresIn * 1000);
        const createdAt = JSON.stringify(now);
        this.authInfo = AuthInfo.createAuthenticated(
          expiresAt,
          createdAt,
          authResult.accessToken,
          authResult.idToken,
          authResult.idTokenPayload,
        );
        this._authInfoSubject.next(this.authInfo);
      } else if (err) {
        // When user visits enin and token has expired, redirecturl will be saved.
        this.trySetRedirectBackUrl();
        if (err.error !== 'login_required') {
          // not sure why this is caught as an error, however this was the easiest solution to show all errors
          // alternatively check for error.error = unauthorized.
          this.authInfo = AuthInfo.createError(err.error_description);
        } else {
          this.trySetRedirectBackUrl();
          this.authInfo = AuthInfo.createUnauthenticated(err.error_description);
        }
        // The url might look like this: #error=unauthorized&error_description=VERIFY_EMAIL&state=7I5GkavjFjrmscOL2Pu.LYUvyyFLQ0OK
        this._authInfoSubject.next(this.authInfo);
      } else {
        this.authInfo = AuthInfo.createError();
        this._authInfoSubject.next(this.authInfo);
      }
    });
  }

  private static _getCurrentPath(): string {
    const { hash, pathname, search } = document.location;
    return `${pathname}${search}${hash}`;
  }

  // TODO: unit tests
  public getToken(): Observable<string> {
    return of(this.authInfo?.isValid() && this.authInfo?.accessToken ? this.authInfo?.accessToken : '');
  }

  public overrideToken(authInfo: AuthInfo): void {
    this._authInfoSubject.next(authInfo);
  }

  public trySetRedirectBackUrl(url: string | null = null): void {
    // Will try to set the redirect URL if it does not already exist
    // Will not set redirect url for the following blacklisted pages to avoid infinite spinner
    // If no redirect url, user will be sent to dashboard.
    const blacklist = [
      'identity',
      'verification-success',
      'sign-up',
      'logout',
      'login',
      'invitation',
      'sso/',
      'eika',
      '404',
      '403',
      'maintenance',
    ];
    const existingRedirectUrl = this._storageService.getItem('auth.redirect_url');
    const currentPath = AppAuthService._getCurrentPath();

    const redirectUrl = url || currentPath || '/';

    if (!existingRedirectUrl && !includesAny(redirectUrl, blacklist)) {
      this.redirectUrl = redirectUrl;
      this._storageService.setItem('auth.redirect_url', redirectUrl);
    }
  }

  private _getAndClearSkipCheckSession(): boolean {
    let skipCheckSession = false;
    if (localStorage.getItem('skip_check_session') === 'true') {
      skipCheckSession = true;
      localStorage.removeItem('skip_check_session');
    }
    return skipCheckSession;
  }

  public login(username: string, password: string, errorCallback: Auth0Callback<unknown>): void {
    localStorage.setItem('user_email', username);
    const params = {
      realm: 'Username-Password-Authentication',
      username: username,
      password: password,
    };
    if (typeof this.webAuth !== 'undefined') {
      this.webAuth.login(params, errorCallback);
    }
  }

  public authorize(connection: string | undefined): void {
    if (typeof this.webAuth !== 'undefined') {
      const options: AuthorizeOptions = { scope: 'openid email profile read:user' };
      if (connection) {
        // Without conenction the auth0 lock screen will be shown.
        options.connection = connection;
      }
      this.webAuth.authorize(options);
    }
  }

  // TODO: what is it for?
  public crossOriginVerification(): void {
    if (typeof this.webAuth !== 'undefined') {
      this.webAuth.crossOriginVerification();
    }
  }

  public resetPasswordRequest(params: ChangePasswordOptions, callback: Auth0Callback<unknown>): void {
    if (typeof this.webAuth !== 'undefined') {
      this.webAuth.changePassword(params, callback);
      // https://auth0.com/docs/api/authentication#change-password
    }
  }

  public logout(
    logoutMessageCode?: keyof typeof LOGIN_MESSAGES | string,
    customMessage?: string,
    federated = false,
    returnToLoginPage = true,
    clearAllLocalStorage = false,
  ): void {
    posthog.capture('$logout', {
      logoutMessageCode,
      customMessage,
      federated,
      authInfoValid: this.authInfo?.isValid(),
    });
    if (logoutMessageCode) {
      if (logoutMessageCode === 'custom' && customMessage) {
        this.loginMessage = customMessage;
      } else if (isLoginMessage(logoutMessageCode) && this.loginMessageDict[logoutMessageCode]) {
        this.loginMessage = this.loginMessageDict[logoutMessageCode];
      }
    }

    const usingIdentityProvider =
      this.authInfo && !!this.authInfo.idTokenPayload && this.authInfo.idTokenPayload.sub.startsWith('waad|');
    const userEmail = localStorage.getItem('user_email');
    // localStorage.clear();  // TODO: What happens to preferences when we do this. Should we really clear preferences when logging out?
    this._storageService.clearAllExceptNamespaces();
    if (userEmail && !usingIdentityProvider && !clearAllLocalStorage) {
      localStorage.setItem('user_email', userEmail);
    }
    if (
      typeof this.loginMessage === 'string' &&
      typeof logoutMessageCode === 'string' &&
      isLoginMessage(logoutMessageCode) &&
      this.loginMessageDict[logoutMessageCode] &&
      !clearAllLocalStorage
    ) {
      localStorage.setItem('login_message', this.loginMessage);
    }
    this.trySetRedirectBackUrl();

    const navigatePromise = this._router.navigate(['/']);
    from(navigatePromise).subscribe({
      next: () => {
        if (typeof this.webAuth !== 'undefined') {
          this.webAuth.logout({
            clientID: this.clientId,
            returnTo: returnToLoginPage ? this.loginUrl : this.logoutUrl,
            federated: federated, // Set this to true to logout of microsoft.
          });
        }
      },
      error: () => {
        if (typeof this.webAuth !== 'undefined') {
          this.webAuth.logout({
            clientID: this.clientId,
            returnTo: returnToLoginPage ? this.loginUrl : this.logoutUrl,
            federated, // Set this to true to logout of microsoft.
          });
          // Not sure what to do. try to redirect anyway
          // this._toastr.error('Could not log you out, please try again.');
        }
      },
    });
  }

  public renewToken(): void {
    const clearSessionIfPossible = !this._getAndClearSkipCheckSession();
    posthog.capture('$renewtoken', {
      clearSessionIfPossible,
    });
    if (typeof this.webAuth !== 'undefined') {
      this.webAuth.checkSession({}, (err, authResult: AuthResult) => {
        if (clearSessionIfPossible && authResult && authResult.accessToken && authResult.idToken) {
          const now = new Date().getTime();
          const expiresAt = JSON.stringify(now + authResult.expiresIn * 1000);
          const createdAt = JSON.stringify(now);
          this.authInfo = AuthInfo.createAuthenticated(
            expiresAt,
            createdAt,
            authResult.accessToken,
            authResult.idToken,
            authResult.idTokenPayload,
          );
          this._authInfoSubject.next(this.authInfo);
        } else if (err) {
          posthog.capture('$renewtokenerror', {
            clearSessionIfPossible,
            err,
          });
          this.authInfo = AuthInfo.createUnauthenticated('Failed to renew session.');
          this._authInfoSubject.next(this.authInfo);
          this.logout('unauthorized'); // Do we need this one?
        } else {
          this.authInfo = AuthInfo.createError();
          this._authInfoSubject.next(this.authInfo);
        }
      });
    }
  }

  public renewTokenIfStale(staleAge = 30 * 60 * 1000): void {
    // staleAge is recommended to be at least 15 minutes, we have here set it to 30 minutes.
    // https://auth0.com/docs/api-auth/tutorials/silent-authentication
    if (this.authInfo && this.authInfo.isOlderThan(staleAge)) {
      this.renewToken();
    }
  }

  // Don't use the following. It is not ready for production, and may never be.
  // public fetchAppAccessToken(username: string, password: string): Observable<AppAccessTokenEntity> {
  //   let params = new HttpParams();
  //   params = params.append('username', username);
  //   params = params.append('password', password);
  //   return this._api.get<AppAccessTokenEntity>('app-access-token', {params: params}, null);
  // }

  // Don't use the following. It is not ready for production, and may never be.
  // public renewAppAccessToken(accessToken: string): Observable<AppAccessTokenEntity> {
  //   let params = new HttpParams();
  //   params = params.append('access_token', accessToken);
  //   return this._api.get<AppAccessTokenEntity>('app-access-token', {params: params}, null);
  // }

  public popRedirectUrl(): string | null {
    const redirectUrl = this.redirectUrl;
    this._storageService.setItem('auth.redirect_url', null);
    this.redirectUrl = null;
    return redirectUrl;
  }

  public getRedirectUrl(): string | null {
    const existingRedirectUrl = this._storageService.getItem('auth.redirect_url');
    if (typeof existingRedirectUrl === 'string') {
      return existingRedirectUrl;
    }
    return this.redirectUrl;
  }
}
